import React, { useContext, useEffect, useState } from "react";
import "./Announcement.scss";
import TextArea from "../../../components/Fields/TextArea";
import { styles, labelStyles, textAreaStyles } from "./AnnouncementStyles";
import { toast } from "react-toastify";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { AppContext } from "../../../context/AppContext";
import api from "../../../api.js";
import Loader from "../../../components/Loader/Loader.jsx";

const Announcement = () => {
  const { backendUrl } = useContext(AppContext);
  const [allAnnouncements, setAllAnnouncements] = useState([]);
  const [announcement, setAnnouncement] = useState("");
  const [visible, setVisible] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [updatedAnnouncement, setUpdatedAnnouncement] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [scrollHeight, setScrollHeight] = useState("425px");
  const [loading, setLoading] = useState(false);

  let errorShown = false;

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerHeight;
      if (screenWidth >= 900) {
        setScrollHeight("650px");
      } else {
        setScrollHeight("440px");
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  async function fetchAnnouncements() {
    setLoading(true);
    try {
      const { data } = await api.get(
        `${backendUrl}/api/auth/getAllAnnouncements`
      );
      if (data.success) {
        setAllAnnouncements(data.announcements || []);
      } else {
        toast.error(data.message);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const validateInput = (text) => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      return "Announcement is required";
    }
    return null;
  };

  async function createAnnouncement(e) {
    e.preventDefault();
    const error = validateInput(announcement);
    if (error) {
      setValidationError(error);
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await api.post(
        `${backendUrl}/api/auth/createAnnouncement`,
        { announcement }
      );
      document.activeElement?.blur();
      if (data.success) {
        toast.success(data.message);
        setAnnouncement("");
        setVisible(false);
        fetchAnnouncements();
        setValidationError(null);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEditClick = (rowData) => {
    setSelectedAnnouncement(rowData);
    setUpdatedAnnouncement(rowData);
    setVisible(true);
  };

  const updateAnnouncement = async (e) => {
    e.preventDefault();
    const error = validateInput(updatedAnnouncement.announcement);
    if (error) {
      setValidationError(error);
      return;
    }
    if (
      !updatedAnnouncement.announcement ||
      updatedAnnouncement.announcement.trim() === ""
    ) {
      toast.error("Announcement content cannot be empty");
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await api.put(
        `${backendUrl}/api/auth/updateAnAnnouncement/${selectedAnnouncement.id}`,
        updatedAnnouncement
      );
      if (data.success) {
        toast.success(data.message);
        fetchAnnouncements();
        setVisible(false);
        setSelectedAnnouncement(null);
        setUpdatedAnnouncement({});
        setValidationError(null);
      } else {
        toast.error(data.message);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const showConfirmDialog = (rowData) => {
    confirmDialog({
      message: (
        <div className="custom-dialog-content">
          <div className="iconParent">
            <i className="pi pi-times"></i>
          </div>
          <div className="contentParent">
            <h2>Are you sure?</h2>
            <div>
              <p>Do you really want to delete this announcement ? </p>
              <p> This process cannot be undone.</p>
            </div>
          </div>
        </div>
      ),
      header: "",
      icon: null,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      acceptClassName: "p-button-danger custom-delete-btn",
      rejectClassName: "p-button-secondary custom-cancel-btn",
      accept: () => {
        handleDeleteClick(rowData);
      },
    });
  };

  async function handleDeleteClick(rowData) {
    setIsSubmitting(true);
    try {
      const { data } = await api.delete(
        `${backendUrl}/api/auth/deleteAnnouncement/${rowData.id}`
      );
      if (data.success) {
        toast.success(data.message);
        fetchAnnouncements();
      } else {
        toast.error(data.message);
      }
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const toggleStatus = async (rowData) => {
    const id = rowData.id;
    const newStatus = !rowData.isActive;
    setIsSubmitting(true);
    try {
      const response = await api.patch(
        `${backendUrl}/api/auth/toggleAnnouncementStatus/${id}`,
        { isActive: newStatus }
      );
      const updatedAnnouncement = response.data.data;
      const updatedList = allAnnouncements.map((announcement) =>
        announcement.id === id ? updatedAnnouncement : announcement
      );
      if (response.data.success) {
        toast.success(response.data.message);
      }
      fetchAnnouncements();
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleRecentStatus = async (rowData) => {
    const id = rowData.id;
    const newStatus = !rowData.recent;
    setIsSubmitting(true);
    try {
      const response = await api.patch(
        `${backendUrl}/api/auth/toggleAnnouncementRecentStatus/${id}`,
        { recent: newStatus }
      );
      const updatedAnnouncement = response.data.data;
      const updatedList = allAnnouncements.map((announcement) =>
        announcement.id === id ? updatedAnnouncement : announcement
      );
      if (response.data.success) {
        toast.success(response.data.message);
      }
      fetchAnnouncements();
      errorShown = false;
    } catch (error) {
      if (errorShown) return;
      errorShown = true;
      if (!navigator.onLine) {
        toast.error("🚫 No internet connection. Please check your network.");
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.request && !error.response) {
        toast.error(
          "⚠️ Network issue or server is unreachable. Try again later."
        );
      } else if (error.response && error.response.status === 404) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="adminAnnoucementMainContainer">
      <Loader visible={isSubmitting} />
      <div className="headerParent">
        <h2>Welcome to Admin Announcement Page</h2>
      </div>

      <Dialog
        header={
          selectedAnnouncement ? "Edit Announcement" : "Post Announcement"
        }
        visible={visible}
        style={{ width: "40vw" }}
        onHide={() => {
          setVisible(false);
          setSelectedAnnouncement(null);
          setUpdatedAnnouncement({});
          setAnnouncement("");
          setValidationError(null);
        }}
        draggable={false}
        resizable={false}
        modal
        className="customAnnouncementDialog"
      >
        <form
          onSubmit={
            selectedAnnouncement ? updateAnnouncement : createAnnouncement
          }
          className="announcementFormContainer"
        >
          <TextArea
            styles={styles}
            inputStyles={textAreaStyles}
            labelStyles={labelStyles}
            required={!selectedAnnouncement}
            customLabelName="Announcement"
            placeholder="Type the announcement"
            value={
              selectedAnnouncement
                ? updatedAnnouncement.announcement
                : announcement
            }
            handleChange={(e) => {
              setValidationError(null);
              if (selectedAnnouncement) {
                setUpdatedAnnouncement({
                  ...updatedAnnouncement,
                  announcement: e.target.value,
                });
              } else {
                setAnnouncement(e.target.value);
              }
            }}
          />

          {validationError && (
            <div className="validation-error">
              <i className="pi pi-exclamation-triangle"></i>
              {validationError}
            </div>
          )}
          <div className="submitBtnParent">
            <button
              type="submit"
              className="submitButton"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="pi pi-spinner pi-spin"></i> Processing...
                </>
              ) : selectedAnnouncement ? (
                "Update Announcement"
              ) : (
                "Create Announcement"
              )}
            </button>
          </div>
        </form>
      </Dialog>

      <div className="uploadBtnsParent">
        <Button
          label="Create New Announcement"
          severity="primary"
          raised
          className="uploadBtn"
          onClick={() => setVisible(true)}
        />
      </div>
      <article className="tableWrapper">
        <div className="scrollableTableContainer">
          <DataTable
            value={allAnnouncements}
            showGridlines
            stripedRows
            className="table"
            emptyMessage="No announcements available"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
            scrollable
            scrollHeight={scrollHeight}
            loading={loading}
          >
            <Column
              header="S.No"
              body={(_, options) => options.rowIndex + 1}
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="announcement"
              header="Announcement"
              className="tableData"
              style={{ minWidth: "375px", maxWidth: "100%" }}
            />
            <Column
              field="createdAt"
              header="Created At"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="updatedAt"
              header="Updated At"
              body={(rowData) =>
                new Date(rowData.updatedAt).toLocaleDateString()
              }
              className="tableData"
              style={{ width: "150px", whiteSpace: "nowrap" }}
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
            />
            <Column
              field="recent"
              header="Recent"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.recent ? "Yes" : "No"}
                    severity={rowData.recent ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleRecentStatus(rowData)}
                  />
                </div>
              )}
            />
            <Column
              field="status"
              header="Status"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="statusBtnParent">
                  <Button
                    label={rowData.isActive ? "Active" : "Deactive"}
                    severity={rowData.isActive ? "success" : "danger"}
                    className="statusBtn"
                    onClick={() => toggleStatus(rowData)}
                  />
                </div>
              )}
            />
            <Column
              header="Action"
              className="tableData"
              headerStyle={{ whiteSpace: "nowrap" }}
              bodyStyle={{ whiteSpace: "nowrap" }}
              body={(rowData) => (
                <div className="action-buttons">
                  <Button
                    label="Edit"
                    severity="success"
                    className="actionBtns"
                    onClick={() => handleEditClick(rowData)}
                  />
                  <Button
                    label="Delete"
                    severity="danger"
                    className="actionBtns"
                    onClick={() => showConfirmDialog(rowData)}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </article>
    </section>
  );
};

export default Announcement;
